import React, { FC, useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { Divider, Heading } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import useDebouncedCallback from 'common/hooks/useDebouncedCallback';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import { InternationalTransferDynamicForm } from '../../components/InternationalTransferDynamicForm';
import { InternationalTransferRequirementsDocument } from './graphql/internationalTransferRequirements.gql';
import locales from './locales';

const FORM_ID = 'additionalInformationForm';

export const InternationalTransferAdditionalInformation: FC<
  StepProps<BankTransferCreationStep>
> = ({ goBack, goNext }) => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferAdditionalInformation',
    );
  }
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferAdditionalInformation',
    );
  }

  const language = locales.getLanguage();
  const {
    bankTransfer: { quote, recipient },
    setBankTransfer,
  } = useBankTransferContext('internationalTransferAdditionalInformation');
  const requirementsQueryInput = useMemo(
    () => ({
      companyProfileId,
      companyUserId,
      internationalTransferQuoteId: quote.internationalTransferQuoteId,
      internationalTransferRecipientId: recipient.id,
      lang: language,
    }),
    [
      companyProfileId,
      companyUserId,
      language,
      quote.internationalTransferQuoteId,
      recipient.id,
    ],
  );
  const { data, error, refetch } = useQuery(
    InternationalTransferRequirementsDocument,
    {
      variables: {
        input: requirementsQueryInput,
      },
    },
  );

  const refetchDebounced = useDebouncedCallback(refetch, 500);
  const handleFormRefresh = useCallback(
    (values: FieldValues) => {
      refetchDebounced({
        input: {
          ...requirementsQueryInput,
          requirements: values,
        },
      });
    },
    [requirementsQueryInput, refetchDebounced],
  );

  const handleFormSubmit = (_type: string, values: FieldValues) => {
    setBankTransfer((prev) => ({
      ...prev,
      additionalInformation: values,
    }));
    goNext();
  };

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetch} />
      </ErrorLayout>
    );
  }

  if (!data) {
    return <Loader />;
  }

  const { internationalTransferRequirements: formConfigs } = data;

  return (
    <Layout.Root>
      <Layout.Body>
        <TrackView pageName="InternationalTransferAdditionalInformation" />
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>
        <Layout.BodyScrollableSection paddingBottom="space-48">
          <Heading alignSelf="flex-start" size="lg" textAlign="left">
            {locales.title}
          </Heading>
          <InternationalTransferDynamicForm
            configs={formConfigs}
            errors={null}
            id={FORM_ID}
            onRefresh={handleFormRefresh}
            onSubmit={handleFormSubmit}
          />
        </Layout.BodyScrollableSection>
        <Layout.FooterContainer>
          <Divider />
          <Layout.FooterButtonsContainer>
            <Button
              form={FORM_ID}
              icon="arrow-right"
              iconPosition="right"
              type="submit"
            >
              {locales.submit}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};
